import { useState, useMemo, useCallback } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { PlayerHeaderProps } from "./types";
import PlayerShotChart from "../PlayerShotChart";
import { CONDENSED_FONT_FAMILY } from "../../constants";
import { isShotDetailData } from "../../../models/TableData";
import { Shot, DEFAULT_FILTERS } from "../../ShotDetailModal/types";
import ShotDetailChart from "../../ShotDetailModal/ShotDetailChart";
import ShotDetailChartDialog from "../../../components/Elements/ExplorerDataGrid/ShotDetailChartDialog";

const PREVIEW_WIDTH = 280;

function parseShotDetailData(data: string): Shot[] {
  const cleaned = data.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ');
  return JSON.parse(cleaned);
}

export default function PlayerHeader({
  playerId,
  firstName,
  lastName,
  age,
  position,
  jerseyNumber,
  height,
  weight,
  shotChartProps,
}: PlayerHeaderProps) {
  const isRichData = useMemo(
    () => isShotDetailData(shotChartProps.data),
    [shotChartProps.data]
  );

  const shots = useMemo(() => {
    if (!isRichData) return [];
    try {
      return parseShotDetailData(shotChartProps.data);
    } catch (e) {
      console.error("Failed to parse shot detail data:", e);
      return [];
    }
  }, [shotChartProps.data, isRichData]);

  const [modalOpen, setModalOpen] = useState(false);

  const handleClick = useCallback(() => {
    if (isRichData) setModalOpen(true);
  }, [isRichData]);

  const handleClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const modalTitle = `${firstName} ${lastName} - Shot Detail`;

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" spacing={3} sx={{ flex: 1, overflow: "hidden" }}>
        <Stack
          sx={{
            overflowX: "hidden",
            alignSelf: "flex-end",
            pt: 1,
          }}
        >
          <Box
            component="img"
            src={`https://cdn.nba.com/headshots/nba/latest/1040x760/${playerId}.png`}
            alt={`${firstName} ${lastName}`}
            sx={{
              height: 200,
              width: 150,
              objectFit: "cover",
              alignSelf: "flex-end",
              filter: `grayscale(100%) brightness(125%) contrast(110%) drop-shadow(2px 2px 0 white) drop-shadow(-2px 2px 0 white) drop-shadow(2px -2px 0 white) drop-shadow(-2px -2px 0 white) drop-shadow(2px 2px 0 black) drop-shadow(-2px 2px 0 black) drop-shadow(2px -2px 0 black) drop-shadow(-2px -2px 0 black)`,
            }}
          />
        </Stack>

        <Stack sx={{ flex: 1 }}>
          <Typography
            variant="h2"
            sx={{
              letterSpacing: -1,
              ml: -2,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              whiteSpace: "nowrap",
              fontFamily: CONDENSED_FONT_FAMILY,
            }}
          >
            <Typography variant="inherit" component="span" fontWeight={200}>
              {firstName}{" "}
            </Typography>
            <Typography variant="inherit" component="span" fontWeight={500}>
              {lastName}
            </Typography>
          </Typography>

          <Stack direction="row" spacing={1}>
            <Typography variant="body1">{position}</Typography>
            <Typography variant="body1">|</Typography>
            <Typography variant="body1">
              #{jerseyNumber === 100 ? "00" : jerseyNumber}
            </Typography>
            <Typography variant="body1">|</Typography>
            <Typography variant="body1">{age.toFixed(1)}</Typography>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Typography variant="body1">
              {Math.floor(height / 12)}'{height % 12}"
            </Typography>
            <Typography variant="body1">|</Typography>
            <Typography variant="body1">{weight} lb</Typography>
          </Stack>
        </Stack>
      </Stack>

      {isRichData ? (
        <>
          <Box
            onClick={handleClick}
            sx={{
              cursor: "pointer",
              borderRadius: 1,
              transition: "opacity 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                opacity: 0.8,
                boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.2)",
              },
            }}
          >
            <ShotDetailChart
              shots={shots}
              filters={DEFAULT_FILTERS}
              width={PREVIEW_WIDTH}
              bgcolor="transparent"
            />
          </Box>
          {modalOpen && (
            <ShotDetailChartDialog
              shots={shots}
              isOpen={modalOpen}
              handleClose={handleClose}
              title={modalTitle}
            />
          )}
        </>
      ) : (
        <PlayerShotChart {...shotChartProps} />
      )}
    </Stack>
  );
}
